import React, { useEffect, useState } from "react";
import { Loader2, KeyRound } from "lucide-react";
import AdminLayout from "@/admin/AdminLayout";
import { adminApi, useAdminAuth } from "@/admin/AdminAuth";
import { toast } from "sonner";

export default function AdminSettings() {
  const { logout } = useAdminAuth();
  const [status, setStatus] = useState(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [mismatch, setMismatch] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/integrations/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      setMismatch(true);
      toast.error("New passwords don't match.");
      return;
    }
    setMismatch(false);
    setSubmitting(true);
    try {
      await adminApi.post("/admin/password", { current, new: next });
      toast.success("Password updated. Please sign in again.");
      setTimeout(logout, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update password.");
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout title="Settings">
      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="rounded-2xl bg-white border border-silver/40 shadow-card p-6" data-testid="admin-password-form">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound size={16} className="text-cyan" />
            <h3 className="text-sm font-bold text-navy">Change password</h3>
          </div>
          <p className="text-xs text-muted mb-5">Minimum 12 characters with a letter, number and symbol.</p>
          <FieldPw label="Current password" value={current} onChange={setCurrent} testId="admin-pw-current" />
          <FieldPw label="New password" value={next} onChange={setNext} testId="admin-pw-new" />
          <FieldPw label="Confirm new password" value={confirm} onChange={setConfirm} testId="admin-pw-confirm" />
          {mismatch && (
            <p
              role="alert"
              data-testid="admin-password-mismatch-error"
              className="-mt-2 mb-3 text-xs text-destructive"
            >
              New passwords don't match.
            </p>
          )}
          <button type="submit" disabled={submitting} data-testid="admin-pw-submit"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-navy text-white h-11 text-sm font-semibold hover:brightness-110 transition disabled:opacity-60">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            Update password
          </button>
        </form>

        <div className="rounded-2xl bg-white border border-silver/40 shadow-card p-6" data-testid="admin-integrations">
          <h3 className="text-sm font-bold text-navy mb-1">Integrations</h3>
          <p className="text-xs text-muted mb-5">Read-only status.</p>
          {!status ? (
            <div className="text-muted text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Checking…</div>
          ) : (
            <ul className="space-y-3 text-sm">
              <Row label="Stripe (test)" ok={status.stripe?.connected} value="connected via platform-managed key" />
              <Row label="Resend" ok={status.resend?.configured} value={status.resend?.mode === "live" ? "live" : "stubbed (logs only)"} />
              <Row label="Sender email" ok={Boolean(status.resend?.sender_email)} value={status.resend?.sender_email || "not set"} />
              <Row label="MongoDB" ok={status.mongodb?.connected} value="connected" />
              <Row label="Admin username" ok value={status.admin_username} />
            </ul>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function FieldPw({ label, value, onChange, testId }) {
  return (
    <div className="mb-4">
      <label className="block text-xs uppercase tracking-widest text-muted mb-2">{label}</label>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full h-11 rounded-xl border border-silver/70 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-cyan"
      />
    </div>
  );
}
function Row({ label, ok, value }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${ok ? "bg-cyan" : "bg-silver"}`} aria-hidden="true" />
        <span className="text-ink">{label}</span>
      </div>
      <span className="text-muted text-xs">{value}</span>
    </li>
  );
}
